import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Plus, Edit, Trash2, Package } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const AdminSubscriptions = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [newSub, setNewSub] = useState({
    user_id: '',
    plan_name: 'Premium',
    plan_type: 'monthly',
    price_usd: 12,
    price_sar: 45,
    status: 'active',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    auto_renew: true
  });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchSubscriptions();
    fetchUsers();
  }, [user, navigate]);

  const fetchSubscriptions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/subscriptions`, {
        headers: {
          'Authorization': `Bearer ${user.email}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.subscriptions || []);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${user.email}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const createSubscription = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/subscriptions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.email}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSub)
      });

      if (response.ok) {
        setDialogOpen(false);
        fetchSubscriptions();
        // Reset form
        setNewSub({
          user_id: '',
          plan_name: 'Premium',
          plan_type: 'monthly',
          price_usd: 12,
          price_sar: 45,
          status: 'active',
          start_date: new Date().toISOString().split('T')[0],
          end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          auto_renew: true
        });
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
    }
  };

  const updatePlanDetails = (planName, planType) => {
    const prices = {
      'Basic': { monthly: { usd: 0, sar: 0 }, yearly: { usd: 0, sar: 0 } },
      'Premium': { monthly: { usd: 12, sar: 45 }, yearly: { usd: 120, sar: 450 } },
      'Advanced': { monthly: { usd: 60, sar: 225 }, yearly: { usd: 600, sar: 2250 } }
    };

    const price = prices[planName][planType];
    const durationDays = planType === 'yearly' ? 365 : 30;
    
    setNewSub({
      ...newSub,
      plan_name: planName,
      plan_type: planType,
      price_usd: price.usd,
      price_sar: price.sar,
      end_date: new Date(Date.now() + durationDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    });
  };

  const cancelSubscription = async (subId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/subscriptions/${subId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${user.email}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'cancelled' })
      });

      if (response.ok) {
        fetchSubscriptions();
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/admin')} variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Subscription
              </Button>
              <Button onClick={handleLogout} variant="destructive">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Subscriptions Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Subscriptions ({subscriptions.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : subscriptions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>No subscriptions found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">User</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Plan</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Price</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">End Date</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {subscriptions.map((sub) => (
                      <tr key={sub.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">
                          <div>
                            <div className="font-medium">{sub.user_name}</div>
                            <div className="text-gray-500 text-xs">{sub.user_email}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">{sub.plan_name}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className="capitalize">{sub.plan_type}</span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div>${sub.price_usd}</div>
                          <div className="text-xs text-gray-500">{sub.price_sar} SAR</div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            sub.status === 'active' ? 'bg-green-100 text-green-700' :
                            sub.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {sub.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {new Date(sub.end_date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => cancelSubscription(sub.id)}
                            disabled={sub.status !== 'active'}
                            className="text-red-600"
                          >
                            Cancel
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Subscription Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Subscription</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>User</Label>
              <Select value={newSub.user_id} onValueChange={(value) => setNewSub({...newSub, user_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Plan</Label>
              <Select value={newSub.plan_name} onValueChange={(value) => updatePlanDetails(value, newSub.plan_type)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Basic">Basic</SelectItem>
                  <SelectItem value="Premium">Premium</SelectItem>
                  <SelectItem value="Advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Billing Type</Label>
              <Select value={newSub.plan_type} onValueChange={(value) => updatePlanDetails(newSub.plan_name, value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Price (USD)</Label>
                <Input type="number" value={newSub.price_usd} readOnly className="bg-gray-50" />
              </div>
              <div className="space-y-2">
                <Label>Price (SAR)</Label>
                <Input type="number" value={newSub.price_sar} readOnly className="bg-gray-50" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" value={newSub.start_date} onChange={(e) => setNewSub({...newSub, start_date: e.target.value})} />
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" value={newSub.end_date} readOnly className="bg-gray-50" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={createSubscription}>Create Subscription</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSubscriptions;
